import React, { createContext, useContext } from 'react';
import { ChevronDown, ChevronUp, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';

// ── Sort types (shared) ───────────────────────────────────────────────────────

export type SortDir = 'asc' | 'desc';

export interface SortState {
  by:  string;
  dir: SortDir;
}

/**
 * "cards" variant renders each row as a standalone rounded card with vertical
 * gaps between rows. Used by the top-level list pages (Equipment / Parts /
 * Work Orders / History) so they feel more like the Schedules card grid while
 * staying tabular for dense fleet data.
 *
 * Default variant keeps the compact "rows in a bordered box" look — right for
 * embedded mini-tables (Reports log, Top Offenders, audit log, etc.).
 */
type TableVariant = 'default' | 'cards';
const TableVariantContext = createContext<TableVariant>('default');

export function Table({
  variant   = 'default',
  className,
  children,
}: {
  variant?:  TableVariant;
  className?: string;
  children:   React.ReactNode;
}) {
  if (variant === 'cards') {
    return (
      <TableVariantContext.Provider value="cards">
        <div className="w-full overflow-x-auto">
          <table
            className={cn(
              'w-full text-sm text-left border-separate',
              '[border-spacing:0_8px]',
              className,
            )}
          >
            {children}
          </table>
        </div>
      </TableVariantContext.Provider>
    );
  }
  return (
    <TableVariantContext.Provider value="default">
      <div className="w-full overflow-x-auto rounded-lg border border-canvas-line">
        <table className={cn('w-full text-sm text-left', className)}>
          {children}
        </table>
      </div>
    </TableVariantContext.Provider>
  );
}

export function Thead({ children }: { children: React.ReactNode }) {
  const variant = useContext(TableVariantContext);
  return (
    <thead
      className={cn(
        'text-[10px] uppercase tracking-[0.08em] text-ink-400 font-semibold',
        variant === 'cards'
          ? ''
          : 'bg-canvas-surface border-b border-canvas-line',
      )}
    >
      {children}
    </thead>
  );
}

export function Tbody({ children }: { children: React.ReactNode }) {
  const variant = useContext(TableVariantContext);
  return (
    <tbody className={cn(variant === 'cards' ? '' : 'divide-y divide-slate-100')}>
      {children}
    </tbody>
  );
}

export function Th({ children, className, ...props }: React.ThHTMLAttributes<HTMLTableCellElement>) {
  const variant = useContext(TableVariantContext);
  return (
    <th
      className={cn(
        variant === 'cards' ? 'px-4 pb-2 pt-0 font-semibold' : 'px-4 py-2.5 font-semibold',
        className,
      )}
      {...props}
    >
      {children}
    </th>
  );
}

/**
 * Clickable column header that drives a list page's sort state.
 *
 * Click cycles: not-sorting-this → asc → desc → asc → desc (clicking the
 * already-active column flips direction; clicking a new column starts at asc).
 *
 * Icon: up/down arrow when active, neutral up-down icon when sortable but
 * inactive (so users see it's clickable without having to hover).
 */
export function SortableTh({
  column,
  sort,
  onSort,
  className,
  children,
  align = 'left',
}: {
  column:    string;
  sort:      SortState;
  onSort:    (next: SortState) => void;
  className?: string;
  children:   React.ReactNode;
  align?:    'left' | 'right';
}) {
  const active   = sort.by === column;
  const nextDir: SortDir =
    active && sort.dir === 'asc' ? 'desc' : 'asc';

  return (
    <Th
      className={cn('cursor-pointer select-none hover:text-ink-700', className)}
      onClick={() => onSort({ by: column, dir: nextDir })}
      aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <span className={cn(
        'inline-flex items-center gap-1',
        align === 'right' && 'flex-row-reverse',
      )}>
        {children}
        {active
          ? (sort.dir === 'asc'
              ? <ChevronUp   size={12} />
              : <ChevronDown size={12} />)
          : <ChevronsUpDown size={12} className="opacity-30" />}
      </span>
    </Th>
  );
}

export function Td({ children, className, ...props }: React.TdHTMLAttributes<HTMLTableCellElement>) {
  return <td className={cn('px-4 py-3', className)} {...props}>{children}</td>;
}

export function Tr({
  children,
  className,
  onClick,
}: {
  children:  React.ReactNode;
  className?: string;
  onClick?:   React.MouseEventHandler<HTMLTableRowElement>;
}) {
  const variant = useContext(TableVariantContext);
  if (variant === 'cards') {
    return (
      <tr
        onClick={onClick}
        className={cn(
          'bg-white shadow-[0_1px_0_#fff_inset,0_1px_2px_rgba(11,18,32,.04)]',
          '[&>td]:border-y [&>td]:border-canvas-line',
          '[&>td:first-child]:border-l [&>td:first-child]:rounded-l-lg',
          '[&>td:last-child]:border-r [&>td:last-child]:rounded-r-lg',
          onClick && [
            'cursor-pointer transition',
            'hover:bg-brand-50/60 hover:shadow-[0_1px_0_#fff_inset,0_8px_24px_-12px_rgba(11,18,32,.18)]',
            '[&:hover>td]:bg-brand-50/60',
          ],
          className,
        )}
      >
        {children}
      </tr>
    );
  }
  return (
    <tr
      className={cn(onClick && 'cursor-pointer hover:bg-brand-50/60', className)}
      onClick={onClick}
    >
      {children}
    </tr>
  );
}
